import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ModalBody } from 'reactstrap';

import { ButtonIcon, Button } from 'shared/components/Buttons';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { colors, logos } from 'shared/defaultTheme';

import { ReactComponent as HelpIcon } from 'assets/icon-help.svg';

import { HelpFormModal, Logo } from './styles';

const TopBarHelp = ({ iconColor }) => {
  const { t } = useTranslation();
  const { encryptedInput, entradaUsuario } = useSelector((state) => state.auth);
  const [showChat] = useState(false);

  const [openHelpModal, setOpenHelpModal] = useState(false);

  const onCloseHelpModal = () => {
    setOpenHelpModal(false);
  };

  const goToOutside = (link) => {
    if (!link.match(/^https?:\/\//i)) {
      window.open(`http://${link}`, '_blank');
      return;
    }
    window.open(link, '_blank');
  };

  const onChatActive = () => {
    const script = document.createElement('script');
    script.id = 'lf-chatservice-lefebvre';
    script.setAttribute('data-align', 'bl');
    script.setAttribute('data-o', window.REACT_CHAT_ORIGIN);
    script.setAttribute('data-u', entradaUsuario);
    script.setAttribute('data-s', window.REACT_CHAT_STRATEGY);
    script.setAttribute('data-bt', 0);
    script.setAttribute('data-jq', 1);
    script.src = window.REACT_LEFEBVRE_CHAT;
    document.body.appendChild(script);
  };

  return (
    <div className="mt-3 tobbar-product-user">
      <ButtonIcon
        title={t('login_support_service')}
        id="icon-help-support"
        className="button-icon button-help-icon"
        Icon={() => (
          <HelpIcon className="topbar__help-icon" fill={iconColor} width={28} height={28} />
        )}
        onClick={() => setOpenHelpModal(true)}
      />
      <HelpFormModal isOpen={openHelpModal} toggle={onCloseHelpModal} size="xl">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseHelpModal}
        />
        <ModalBody>
          <div className="top-help-modal-container">
            <div className="d-flex align-items-center justify-content-between">
              <h2 className="top-help-title">{t('login_support_service')}</h2>
              <Logo imageSrc={logos.lefebvre} />
            </div>
            <div className="d-flex my-2 mb-5 justify-content-center">
              <div className="top-info-box mr-5">
                <div className="py-4 top-info-container">
                  <span className="lf-icon-call lf-icon" />
                  <h3 className="help-sub-title mt-2">{t('top_bar_help_telephone_title')}</h3>
                  <p className="help-text-info">{t('top_bar_help_telephone_text')}</p>
                  <div className="d-flex flex-column align-items-center">
                    <span className="help-contact-info">91 210 80 00</span>
                    <span className="help-contact-info">Fax: 91 578 16 17 / 91 210 80 01</span>
                    <span className="help-time-info mt-3">{t('top_bar_help_telephone_days')}</span>
                  </div>
                </div>
              </div>
              <div className="top-info-box mr-5">
                <div className="py-4 top-info-container">
                  <span className="lf-icon-newsletter lf-icon" />
                  <h3 className="help-sub-title mt-2">{t('top_bar_help_mail_title')}</h3>
                  <p className="help-text-info">{t('top_bar_help_mail_text')}</p>
                  <div className="d-flex flex-column align-items-center">
                    <span className="help-contact-info mb-5">portaldelcliente@lefebvre.es</span>
                    <a href="mailto:portaldelcliente@lefebvre.es" className="btn-send-mail">
                      {t('top_bar_help_send_email')}
                    </a>
                  </div>
                </div>
              </div>
              {showChat && (
                <div className="top-info-box mr-5">
                  <div className="py-4 top-info-container">
                    <span className="lf-icon-chat lf-icon" />
                    <h3 className="help-sub-title mt-2">{t('top_bar_help_chat_online_title')}</h3>
                    <p className="help-text-info">{t('top_bar_help_chat_online_text')}</p>
                    <div className="d-flex flex-column align-items-center text-center">
                      <span className="help-time-info mt-3 mb-2">
                        {t('top_bar_help_chat_online_date')}
                      </span>
                      <Button
                        label="Activar chat"
                        className="btn-send-mail btn-chat-active"
                        onClick={() => onChatActive()}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="top-info-box center-help-container mr-3">
                <div className="py-4 top-info-container">
                  <span className="lf-icon-support lf-icon" />
                  <h3 className="help-sub-title mt-2 title-center-help">
                    {t('top_bar_help_service_center_title')}
                  </h3>
                  <div className="center-help-info-container">
                    <div
                      aria-hidden
                      className="center-help-info-box"
                      onClick={() =>
                        goToOutside(
                          `${window.REACT_HELP_CENTER}preguntas-frecuentes/?encrypt_entry=${encryptedInput}`
                        )
                      }
                    >
                      <span className="lf-icon-question center-help-lf-icon mb-2" />
                      <span className="center-help-text">
                        {t('top_bar_help_frequent_questions')}
                      </span>
                    </div>
                    <div
                      className="center-help-info-box"
                      aria-hidden
                      onClick={() =>
                        goToOutside(
                          `${window.REACT_HELP_CENTER}cursos/?encrypt_entry=${encryptedInput}`
                        )
                      }
                    >
                      <span className="lf-icon-documentation center-help-lf-icon mb-2" />
                      <span className="center-help-text">{t('top_bar_help_courses')}</span>
                    </div>
                    <div
                      className="center-help-info-box"
                      aria-hidden
                      onClick={() =>
                        goToOutside(
                          `${window.REACT_HELP_CENTER}videos/?encrypt_entry=${encryptedInput}`
                        )
                      }
                    >
                      <span className="lf-icon-video center-help-lf-icon mb-2" />
                      <span className="center-help-text">{t('top_bar_help_videos')}</span>
                    </div>
                    <div
                      className="center-help-info-box"
                      aria-hidden
                      onClick={() =>
                        goToOutside(
                          `${window.REACT_HELP_CENTER}manuales/?encrypt_entry=${encryptedInput}`
                        )
                      }
                    >
                      <span className="lf-icon-document center-help-lf-icon mb-2" />
                      <span className="center-help-text">{t('top_bar_help_manuals')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-center">
              <div
                className="btn-go-center-help"
                aria-hidden
                onClick={() =>
                  goToOutside(`${window.REACT_HELP_CENTER}?encrypt_entry=${encryptedInput}`)
                }
              >
                {t('top_bar_help_go_center_help')}
              </div>
            </div>
          </div>
        </ModalBody>
      </HelpFormModal>
    </div>
  );
};

TopBarHelp.propTypes = {
  iconColor: PropTypes.string.isRequired,
};

export default TopBarHelp;
